<template>
  <a-popover
    v-model="visible"
    trigger="click"
    placement="bottomRight"
    overlay-class-name="erp-column-setting-popover"
  >
    <template slot="content">
      <div class="column-setting">
        <div class="column-setting__header">
          <span>列设置</span>
          <a @click="handleReset">重置</a>
        </div>
        <div class="column-setting__list">
          <div
            v-for="(item, index) in items"
            :key="item.id"
            class="column-setting__item"
          >
            <a-checkbox
              :checked="item.visible"
              :disabled="item.locked"
              @change="e => onToggle(item, e.target.checked)"
            >
              {{ item.title }}
            </a-checkbox>
            <div v-if="!item.locked" class="column-setting__moves">
              <a-icon
                type="up"
                :class="{ disabled: !canMoveUp(index) }"
                @click="moveUp(index)"
              />
              <a-icon
                type="down"
                :class="{ disabled: !canMoveDown(index) }"
                @click="moveDown(index)"
              />
            </div>
            <span v-else class="column-setting__lock">固定</span>
          </div>
        </div>
      </div>
    </template>
    <a-icon type="setting" class="column-setting-trigger" title="列设置" />
  </a-popover>
</template>

<script>
import { crud } from "@crud/crud"
import {
  buildSetting,
  getSettingItems
} from "@/utils/column-setting"
import { mapGetters } from "vuex"

export default {
  name: "ColumnSetting",
  mixins: [crud()],
  props: {
    tableKey: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      visible: false,
      items: []
    }
  },
  computed: {
    ...mapGetters(["user"]),
    userId() {
      return (this.user && this.user.userId) || "anonymous"
    },
    resolvedTableKey() {
      return (
        this.tableKey ||
        (this.$route && this.$route.name) ||
        this.crud.title ||
        "default"
      )
    },
    originalColumns() {
      return (
        (this.crud.props && this.crud.props.originalColumns) ||
        this.crud.columns ||
        []
      )
    }
  },
  watch: {
    resolvedTableKey: {
      immediate: true,
      handler(key) {
        if (!key || !this.crud) return
        this.crud.initColumnSetting(key, this.userId)
        this.refreshItems()
      }
    }
  },
  methods: {
    refreshItems() {
      const setting =
        (this.crud.props && this.crud.props.columnSetting) || null
      this.items = getSettingItems(this.originalColumns, setting)
    },
    currentSettingFromItems() {
      const unlocked = this.items.filter(i => !i.locked)
      const visibleOrder = unlocked.map(i => i.id)
      const hiddenIds = unlocked.filter(i => !i.visible).map(i => i.id)
      const prev = (this.crud.props && this.crud.props.columnSetting) || {}
      return buildSetting(
        this.originalColumns,
        visibleOrder,
        hiddenIds,
        prev.widths || {}
      )
    },
    persist() {
      const setting = this.currentSettingFromItems()
      this.crud.applyColumns(setting, this.userId)
      this.refreshItems()
    },
    onToggle(item, checked) {
      if (item.locked) return
      item.visible = checked
      // 至少保留一列可见（非锁定）
      const unlockedVisible = this.items.filter(i => !i.locked && i.visible)
      if (!unlockedVisible.length) {
        item.visible = true
        this.$message &&
          this.$message.warning("至少保留一列显示")
        return
      }
      this.persist()
    },
    canMoveUp(index) {
      const item = this.items[index]
      if (!item || item.locked) return false
      if (index <= 0) return false
      return !this.items[index - 1].locked
    },
    canMoveDown(index) {
      const item = this.items[index]
      if (!item || item.locked) return false
      const next = this.items[index + 1]
      if (!next || next.locked) return false
      return true
    },
    moveUp(index) {
      if (!this.canMoveUp(index)) return
      const list = this.items.slice()
      const tmp = list[index - 1]
      list[index - 1] = list[index]
      list[index] = tmp
      this.items = list
      this.persist()
    },
    moveDown(index) {
      if (!this.canMoveDown(index)) return
      const list = this.items.slice()
      const tmp = list[index + 1]
      list[index + 1] = list[index]
      list[index] = tmp
      this.items = list
      this.persist()
    },
    handleReset() {
      this.crud.resetColumns(this.userId)
      this.refreshItems()
      this.$message && this.$message.success("已恢复默认列设置")
    }
  }
}
</script>

<style lang="scss">
.erp-column-setting-popover {
  .ant-popover-inner-content {
    padding: 0;
  }
}
</style>

<style lang="scss" scoped>
.column-setting {
  width: 260px;
  max-height: 420px;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid #eef2f7;
    font-weight: 600;
    color: #1a2333;
    font-size: 13px;

    a {
      font-size: 12px;
      font-weight: 500;
    }
  }

  &__list {
    overflow: auto;
    padding: 4px 0;
    max-height: 360px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    transition: background 0.15s;

    &:hover {
      background: #f5f8fc;
    }

    ::v-deep .ant-checkbox-wrapper {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 13px;
      color: #1a2333;
    }
  }

  &__moves {
    display: flex;
    align-items: center;
    margin-left: 8px;

    .anticon {
      font-size: 12px;
      color: #6b7a90;
      cursor: pointer;
      padding: 2px;
      border-radius: 4px;
      transition: all 0.15s;

      &:hover:not(.disabled) {
        color: #1890ff;
        background: #e8f3ff;
      }

      &.disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      & + .anticon {
        margin-left: 4px;
      }
    }
  }

  &__lock {
    font-size: 12px;
    color: #bfbfbf;
    margin-left: 8px;
  }
}

.column-setting-trigger {
  cursor: pointer;
}
</style>
